import React, { useEffect, useState, useRef } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";

import LogoLight from "../assets/images/sattva-logo.png";
import LogoDark from "../assets/images/sattva-logo.png";

export default function Navbar({ navdark }) {
  const [isOpen, setMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById("navbar");
      if (navbar) {
        if (window.scrollY >= 50) {
          navbar.classList.add("is-sticky");
        } else {
          navbar.classList.remove("is-sticky");
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.scrollTo(0, 0); // scroll to top on mount

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setMenu((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!menuRef.current) return;

      // If click is OUTSIDE the menu container → close
      if (!menuRef.current.contains(event.target)) {
        setMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  useEffect(() => {
    setMenu(false);
  }, [location.pathname]);

  return (
    <nav className="navbar" id="navbar">
      <div
        className="container flex flex-wrap items-center justify-between"
        ref={menuRef}
      >
        {/* Logo */}
        <Link className="navbar-brand" to="/">
          {navdark ? (
            <>
              <img
                src={LogoDark}
                className="inline-block dark:hidden w-100 h-10"
                alt="Sattva Logo"
              />
              <img
                src={LogoLight}
                className="hidden dark:inline-block w-100 h-10"
                alt="Sattva Logo"
              />
            </>
          ) : (
            <>
              <span className="inline-block dark:hidden">
                <img
                  src={LogoDark}
                  className="l-dark w-100 h-10"
                  alt="Sattva Logo"
                />
                <img
                  src={LogoLight}
                  className="l-light w-100 h-10"
                  alt="Sattva Logo"
                />
              </span>
              <img
                src={LogoLight}
                className="hidden dark:inline-block w-100 h-10"
                alt="Sattva Logo"
              />
            </>
          )}
        </Link>

        {/* Social Icons + Mobile Menu Button */}
        <div className="nav-icons flex items-center lg_992:order-2 ms-auto">
          <ul className="list-none menu-social mb-0 flex items-center">
            <li className="inline ms-1">
              <Link
                to="https://www.linkedin.com/company/sattva-safe-sustainable/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-icon rounded-full bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 text-white"
              >
                <i className="uil uil-linkedin"></i>
              </Link>
            </li>
            <li className="inline ms-1">
              <Link
                to="https://www.instagram.com/sattva.esg?igsh=MWxhajI1eDNld29pdg=="
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-icon rounded-full bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 text-white"
              >
                <i className="uil uil-instagram"></i>
              </Link>
            </li>
            <li className="inline ms-1">
              <Link
                to="https://www.youtube.com/@redefinegroupofcompanies"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-icon rounded-full bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 text-white"
              >
                <i className="uil uil-youtube"></i>
              </Link>
            </li>
          </ul>

          {/* Mobile Menu Toggle */}
          <div>
            <button
              type="button"
              className="collapse-btn inline-flex items-center ms-3 text-dark dark:text-white lg_992:hidden"
              onClick={toggleMenu}
              aria-label="Toggle navigation"
            >
              <i className="mdi mdi-menu mdi-24px"></i>
            </button>
          </div>
        </div>

        {/* Navigation Links */}
        <div
          className={`${isOpen ? "block" : "hidden"} navigation lg_992:order-1 lg_992:flex lg_992:items-center`}
          id="menu-collapse"
        >
          <ul
            className={`navbar-nav ${navdark ? "" : "nav-light"} flex flex-col lg_992:flex-row`}
            id="navbar-navlist"
          >
            <li>
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `nav-item nav-link p-0 ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-link">Home</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `nav-item nav-link p-0 ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-link">About us</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/learning-and-insights"
                className={({ isActive }) =>
                  `nav-item nav-link p-0 ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-link">Learning & insights</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/products"
                className={({ isActive }) =>
                  `nav-item nav-link p-0 ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-link">Products</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/our-team"
                className={({ isActive }) =>
                  `nav-item nav-link p-0 ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-link">Our Team</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  `nav-item nav-link p-0 ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-link">Contact Us</span>
              </NavLink>
            </li>

            {/* Book a Demo Button – now correctly links to /contact */}
            <li className="mt-2 lg_992:mt-0 lg_992:ms-4">
              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  `nav-item nav-link p-0 ${isActive ? "active" : ""}`
                }
              >
                <button
                  className="nav-link text-white rounded-lg transition bg-primary hover:bg-primary/90 focus:ring-4 focus:outline-none focus:ring-primary/50 border border-transparent font-medium text-sm px-5 py-2.5 text-center inline-flex items-center dark:focus:ring-primary/55"
                  onClick={() => navigate("/contact")}
                >
                  Book a demo
                </button>
              </NavLink>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}
